import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../components/context/AuthContext';
import { useNotifications } from '../../components/context/NotificationContext';
import PushNotificationControl from './PushNotificationControl';

const AdminHeaderNavigation = ({ userName, isSidebarCollapsed = false }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const { logout, user } = useAuth();
  const isAdmin = user?.roles?.some((role) => String(role).toLowerCase() === 'admin');
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications();

  const handleLogout = async () => {
    await logout(); 
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  return (
    <header className={`fixed left-0 right-0 top-0 z-30 border-b border-border bg-card shadow-sm transition-[left] duration-250 ease-elegant ${
      isSidebarCollapsed ? 'lg:left-20' : 'lg:left-64'
    }`}>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="ml-14 lg:ml-0 font-heading font-semibold text-xl text-foreground">
              {isAdmin ? 'Admin Dashboard' : 'Staff Workspace'}
            </h1>
            <span className="hidden md:inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-caption rounded-full">
              {user?.company?.name}
            </span>
          </div>

          <div className="flex items-center gap-4">
            <Link
              to={`/${user?.company?.companyPath}`}
              className="flex items-center gap-2 px-4 py-2 text-foreground hover:bg-muted rounded-lg transition-all duration-250 ease-elegant hover:scale-105 active:scale-95"
              aria-label="View client booking page"
            >
              <Icon name="ExternalLink" size={20} />
              <span className="hidden md:inline font-caption font-medium">Client View</span>
            </Link>

            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-foreground hover:bg-muted rounded-lg transition-all duration-250 ease-elegant hover:scale-105 active:scale-95"
                aria-label="Notifications"
              >
                <Icon name="Bell" size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowNotifications(false)}
                    aria-hidden="true"
                  />
                  <div className="fixed left-1/2 top-20 z-[60] w-[min(24rem,calc(100vw-1.5rem))] -translate-x-1/2 bg-popover border border-border rounded-lg shadow-lg animate-scale-in max-h-[70vh] custom-scrollbar overflow-y-auto sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-1 sm:w-80 sm:max-w-none sm:translate-x-0">
                    <div className="p-4 border-b border-border">
                      <div className="flex items-center justify-between">
                        <h3 className="font-heading font-semibold text-foreground">Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllRead}
                            className="text-sm text-primary hover:text-primary/80 font-caption"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>
                      <PushNotificationControl />
                    </div>
                    <div className="p-2">
                      {notifications.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground">
                          <Icon name="Bell" size={24} className="mx-auto mb-2 opacity-50" />
                          <p className="font-caption">No notifications yet</p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`p-3 mb-2 rounded-lg border cursor-pointer transition-colors ${
                              notification.read
                                ? 'bg-muted/50 border-border'
                                : 'bg-primary/5 border-primary/20'
                            }`}
                            onClick={() => markRead(notification.id)}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                                notification.type === 'booking' ? 'bg-blue-500' : 'bg-green-500'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <p className="font-caption font-medium text-foreground text-sm">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {notification.detail}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {notification.referenceCode}
                                </p>
                                <p className="text-xs text-muted-foreground mt-2">
                                  {new Date(notification.timestamp).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                onClick={toggleUserMenu}
                className="flex items-center gap-3 px-3 py-2 hover:bg-muted rounded-lg transition-all duration-250 ease-elegant"
                aria-label="User menu"
                aria-expanded={showUserMenu}
              >
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                  <Icon name="User" size={18} color="white" />
                </div>
                <span className="hidden md:inline font-caption font-medium text-foreground">
                  {user.firstName || 'Admin'}
                </span>
                <Icon name="ChevronDown" size={16} />
              </button>

              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowUserMenu(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-lg shadow-lg z-50 animate-scale-in">
                    <div className="p-3 border-b border-border">
                      <p className="font-caption font-medium text-foreground">{user.firstName} {user.lastName}</p>
                      <p className="text-sm text-muted-foreground font-caption">{user.email}</p>
                    </div>
                    <nav className="p-2">
                      <Link
                        to=""
                        className="flex items-center gap-3 px-3 py-2 text-foreground hover:bg-muted rounded-lg transition-colors duration-250 cursor-not-allowed"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Icon name="User" size={18} />
                        <span className="font-caption">Profile</span>
                        <i className='bi bi-lock'></i>
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center gap-3 px-3 py-2 text-foreground hover:bg-muted rounded-lg transition-colors duration-250"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Icon name="Settings" size={18} />
                        <span className="font-caption">Settings</span>
                      </Link>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          onLogout && onLogout();
                        }}
                        className="flex items-center gap-3 px-3 py-2 w-full text-error hover:bg-error/10 rounded-lg transition-colors duration-250"
                      >
                        <Icon name="LogOut" size={18} />
                        <span onClick={handleLogout} className="font-caption">Logout</span>
                      </button>
                    </nav>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeaderNavigation;
